import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Check, X } from "lucide-react";

const useStyles = makeStyles({
  switch: {
    "--switch-width": "46px",
    "--switch-height": "24px",
    "--switch-bg": "rgb(131, 131, 131)",
    "--switch-checked-bg": "rgb(0, 218, 80)",
    "--switch-offset":
      "calc((var(--switch-height) - var(--circle-diameter)) / 2)",
    "--switch-transition": "all .2s cubic-bezier(0.27, 0.2, 0.25, 1.51)",
    "--circle-diameter": "18px",
    "--circle-bg": "#fff",
    "--circle-shadow": "1px 1px 2px rgba(146, 146, 146, 0.45)",
    "--circle-checked-shadow": "-1px 1px 2px rgba(163, 163, 163, 0.45)",
    "--circle-transition": "var(--switch-transition)",
    "--icon-transition": "all .2s cubic-bezier(0.27, 0.2, 0.25, 1.51)",
    "--icon-cross-color": "var(--switch-bg)",
    "--icon-cross-size": "6px",
    "--icon-checkmark-color": "var(--switch-checked-bg)",
    "--icon-checkmark-size": "10px",
    "--effect-width": "calc(var(--circle-diameter) / 2)",
    "--effect-height": "calc(var(--effect-width) / 2 - 1px)",
    "--effect-bg": "var(--circle-bg)",
    "--effect-border-radius": "1px",
    "--effect-transition": "all .2s ease-in-out",
    display: "inline-block",
    margin: 0,
    "& input": {
      display: "none",
    },
    "& svg": {
      transition: "var(--icon-transition)",
      position: "absolute",
      height: "auto",
    },
    "& input:checked + $slider": {
      background: "var(--switch-checked-bg)",
    },
    "& input:checked + $slider $checkmark": {
      transform: "scale(1)",
    },
    "& input:checked + $slider $cross": {
      transform: "scale(0)",
    },
    "& input:checked + $slider::before": {
      left:
        "calc(100% - var(--effect-width) - (var(--effect-width) / 2) - var(--switch-offset))",
    },
    "& input:checked + $slider $circle": {
      left: "calc(100% - var(--circle-diameter) - var(--switch-offset))",
      boxShadow: "var(--circle-checked-shadow)",
    },
  },
  slider: {
    boxSizing: "border-box",
    width: "var(--switch-width)",
    height: "var(--switch-height)",
    background: "var(--switch-bg)",
    borderRadius: 999,
    display: "flex",
    alignItems: "center",
    position: "relative",
    transition: "var(--switch-transition)",
    cursor: "pointer",
    "&::before": {
      content: '""',
      position: "absolute",
      width: "var(--effect-width)",
      height: "var(--effect-height)",
      left: "calc(var(--switch-offset) + (var(--effect-width) / 2))",
      background: "var(--effect-bg)",
      borderRadius: "var(--effect-border-radius)",
      transition: "var(--effect-transition)",
    },
  },
  circle: {
    width: "var(--circle-diameter)",
    height: "var(--circle-diameter)",
    background: "var(--circle-bg)",
    borderRadius: "inherit",
    boxShadow: "var(--circle-shadow)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "var(--circle-transition)",
    zIndex: 1,
    position: "absolute",
    left: "var(--switch-offset)",
  },
  cross: {
    width: "var(--icon-cross-size)",
    color: "var(--icon-cross-color)",
    transform: "scale(1)",
  },
  checkmark: {
    width: "var(--icon-checkmark-size)",
    color: "var(--icon-checkmark-color)",
    transform: "scale(0)",
  },
});

const CustomToggleSwitch = ({ checked, defaultChecked, onChange, className, ...rest }) => {
  const classes = useStyles();
  const checkedProps =
    checked !== undefined ? { checked: !!checked } : { defaultChecked: !!defaultChecked };

  return (
    <label
      className={[classes.switch, className].filter(Boolean).join(" ")}
      {...rest}
    >
      <input
        type="checkbox"
        {...checkedProps}
        onChange={(e) => onChange && onChange(e)}
      />
      <div className={classes.slider}>
        <div className={classes.circle}>
          <X size={6} className={classes.cross} strokeWidth={3} />
          <Check size={10} className={classes.checkmark} strokeWidth={3} />
        </div>
      </div>
    </label>
  );
};

export default CustomToggleSwitch;
